/**
 * 主題建立 — Mode A 問答式
 *
 * 對應「不是每個人能一次寫完整觀念」的場景：AI 引導用戶逐題回答，
 * 累積完整 SubjectDraft 後交棒給 SubjectOneClickDialog 走 preview / 採納流程。
 *
 * 流程：
 * 1. user 點「✨ AI 對話建立」開啟此 dialog
 * 2. AI 自動丟第一個問題（背景觸發 conversation API w/ empty messages）
 * 3. user 答 → 累積到 messages → 再次呼叫 → AI 回 next_question 或 ready=true
 * 4. ready=true → onReady(state) 把完整 draft 丟給 parent，parent 開 OneClickDialog 進 preview
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Stack, Button, IconButton, Typography,
  Paper, CircularProgress, Alert, TextField, LinearProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  AutoAwesome as MagicIcon, Send as SendIcon,
  Close as CloseIcon, Done as DoneIcon,
  SmartToy as AiIcon, Person as UserIcon,
} from '@mui/icons-material';

import { useMessage } from '../../contexts/MessageContext';
import {
  draftSubjectConversation,
  DraftConversationMessage,
} from '../../services/researchService';
import { SubjectDraft } from '../../types/research';

interface Props {
  open: boolean;
  onClose: () => void;
  // ready=true 時觸發；parent 拿到完整 draft 後開 SubjectOneClickDialog 進 preview
  onReady: (draft: SubjectDraft) => void;
}

const SubjectConversationDialog: React.FC<Props> = ({ open, onClose, onReady }) => {
  const { showApiError } = useMessage();

  const [messages, setMessages] = useState<DraftConversationMessage[]>([]);
  const [state, setState] = useState<Partial<SubjectDraft>>({});
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [readyDraft, setReadyDraft] = useState<SubjectDraft | null>(null);

  const scrollRef = useRef<HTMLDivElement | null>(null);

  // 對話框打開時自動開場：丟空 messages，AI 回第一題
  useEffect(() => {
    if (open && messages.length === 0 && !loading) {
      askNext([], {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // 自動 scroll 到底
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const reset = () => {
    setMessages([]);
    setState({});
    setInput('');
    setReady(false);
    setReadyDraft(null);
  };

  const handleClose = () => {
    if (loading) return;
    reset();
    onClose();
  };

  const askNext = async (
    currentMessages: DraftConversationMessage[],
    currentState: Partial<SubjectDraft>,
  ) => {
    setLoading(true);
    try {
      const resp = await draftSubjectConversation(currentMessages, currentState);
      // 累積 state
      setState(resp.state);
      if (resp.ready) {
        // 完整 draft 達成；防呆：保證兩個陣列存在
        const normalizedDraft: SubjectDraft = {
          subject_code: resp.state.subject_code ?? '',
          subject_name: resp.state.subject_name ?? '',
          description: resp.state.description ?? '',
          suggested_audiences: resp.state.suggested_audiences ?? [],
          suggested_topics: resp.state.suggested_topics ?? [],
        };
        setReady(true);
        setReadyDraft(normalizedDraft);
        const audN = normalizedDraft.suggested_audiences.length;
        const topN = normalizedDraft.suggested_topics.length;
        // 在 chat 加一個收尾訊息（順帶提示拆出多少筆，user 看得到）
        setMessages([...currentMessages, {
          role: 'assistant',
          content: `✅ 規劃完整了！拆出 ${audN} 個對象 / ${topN} 個議題。點下方「進入草稿預覽」進入採納步驟。`,
        }]);
      } else if (resp.next_question) {
        // 把問題放進 chat
        setMessages([...currentMessages, {
          role: 'assistant',
          content: resp.next_question,
        }]);
      }
    } catch (err: any) {
      showApiError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    const userText = input.trim();
    if (!userText || loading || ready) return;
    setInput('');
    const newMsgs: DraftConversationMessage[] = [
      ...messages,
      { role: 'user', content: userText },
    ];
    setMessages(newMsgs);
    askNext(newMsgs, state);
  };

  const handleSkip = () => {
    // 用戶覺得夠了 / 想直接建：發一個 user 訊息「直接建吧」逼 AI 收尾
    if (loading || ready) return;
    const newMsgs: DraftConversationMessage[] = [
      ...messages,
      { role: 'user', content: '我提供的資訊應該夠了，請整合並準備建立主題。' },
    ];
    setMessages(newMsgs);
    askNext(newMsgs, state);
  };

  const handleEnterPreview = () => {
    if (!readyDraft) return;
    onReady(readyDraft);
    reset();
    onClose();
  };

  // 進度估算：state 含的 key 數量 / 5
  const filledCount = (() => {
    let n = 0;
    if (state.subject_name) n++;
    if (state.description) n++;
    if ((state.suggested_audiences?.length ?? 0) > 0) n++;
    if ((state.suggested_topics?.length ?? 0) > 0) n++;
    // 第 5 個我們把 sublength 大於 3 視為「素材夠完整」
    if ((state.suggested_audiences?.length ?? 0) >= 2
        && (state.suggested_topics?.length ?? 0) >= 3) n++;
    return n;
  })();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <MagicIcon color="primary" />
          ✨ AI 對話建立主題
          <Box sx={{ flexGrow: 1 }} />
          <IconButton size="small" onClick={handleClose} disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          AI 會逐題引導；不必一次寫清楚，邊聊邊累積到完整草稿
        </Typography>
        <Box sx={{ mt: 1 }}>
          <LinearProgress variant="determinate" value={filledCount * 20}
                          sx={{ height: 4, borderRadius: 1 }} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            進度 {filledCount}/5 — {ready ? '已收齊' : '繼續答題'}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box ref={scrollRef}
             sx={{ height: 400, overflowY: 'auto', px: 1 }}>
          <Stack spacing={1.5}>
            {messages.map((m, i) => (
              <Stack key={i} direction="row" spacing={1}
                     alignItems="flex-start"
                     justifyContent={m.role === 'user' ? 'flex-end' : 'flex-start'}>
                {m.role === 'assistant' && <AiIcon color="primary" />}
                <Paper variant="outlined" sx={{
                  p: 1.5, maxWidth: '75%',
                  bgcolor: m.role === 'user' ? 'primary.light' : 'background.paper',
                  color: m.role === 'user' ? 'primary.contrastText' : 'text.primary',
                  whiteSpace: 'pre-wrap',
                }}>
                  <Typography variant="body2">{m.content}</Typography>
                </Paper>
                {m.role === 'user' && <UserIcon color="action" />}
              </Stack>
            ))}
            {loading && (
              <Stack direction="row" spacing={1} alignItems="center">
                <AiIcon color="primary" />
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  AI 思考中…
                </Typography>
              </Stack>
            )}
          </Stack>
        </Box>

        {ready && (
          <Alert severity="success" sx={{ mt: 2 }}>
            草稿到位！按右下「進入草稿預覽」進到一鍵建立步驟。
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', p: 2, gap: 1 }}>
        {!ready && (
          <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
            <TextField
              size="small" fullWidth
              placeholder="回答這個問題..."
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              disabled={loading || ready}
              autoFocus
              multiline
              maxRows={3}
            />
            <Button variant="contained"
                    onClick={handleSend}
                    disabled={loading || !input.trim() || ready}
                    startIcon={loading ? <CircularProgress size={14} /> : <SendIcon />}>
              送出
            </Button>
          </Stack>
        )}

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button onClick={handleClose} disabled={loading}>取消</Button>
          {!ready && messages.filter(m => m.role === 'user').length >= 2 && (
            <Button onClick={handleSkip} disabled={loading} variant="outlined">
              我答夠了，直接生成
            </Button>
          )}
          {ready && (
            <Button onClick={handleEnterPreview} variant="contained" color="primary"
                    startIcon={<DoneIcon />}>
              進入草稿預覽
            </Button>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default SubjectConversationDialog;
